/**
 * SCK-697 child #1 — public /redeem?token=<raw> landing page.
 *
 * The invitation email built by
 *   InvitationsService.buildRedemptionUrl
 * (coord) ships URLs shaped
 *   https://console.clue2.app/redeem?token=<raw>
 * Until this page existed, clicking one hit "Not Found" — no React
 * Router match. That blocked the entire signup flow.
 *
 * Flow:
 *   1. Extract ?token=<raw>. Missing → friendly error card + /login link.
 *   2. GET /api/nda/current (public, whitelisted on coord) to pin the
 *      NDA version the caller will attest to.
 *   3. GET /api/invitations/preview?token=<raw> if the endpoint exists
 *      (ships in the parallel coord PR — child #2). If it 404s or the
 *      network fails we simply skip prefill and show a generic "your
 *      invitation email" label. The page still works.
 *   4. Password form (12+ chars, upper/lower/digit/symbol per SCK-697
 *      child #3) + confirm-password + NDA checkbox.
 *   5. Submit → POST /api/invitations/redeem with
 *        { token, ndaAccepted:true, ndaVersion, password }
 *      On 200: render an inline success card + auto-navigate to /login
 *      after a short pause. On 4xx: map error_code (SCK-695 taxonomy)
 *      to a friendly inline error; fall back to the server message,
 *      then to a generic string.
 *   6. "Sign in with Google instead" — secondary path. Fires the
 *      standard Google-OAuth login; the parallel coord PR auto-
 *      provisions invited users on their first OAuth landing.
 *
 * This route is mounted OUTSIDE the ProtectedRoutes/PermissionsProvider
 * wrapper in App.js — the visitor is not yet a real user, so any
 * `/api/me/*` fetch would 401 and drop them onto /login.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";

import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { API_URL } from "../helpers/constants";
import { authWithGoogle } from "../helpers/utils";
import { setToken, setUserData } from "../helpers/tokenStorage";
import { jwtDecode } from "jwt-decode";
import { brandButton, darkSurface } from "../helpers/designSystem";

const PW_MIN_LEN = 12;

// Local mirror of the SCK-697 child #3 rule set. The server is the
// authority — this only decides whether we let Submit fire the POST.
// If server-side validation still rejects, we surface its error inline.
export const validatePassword = (pw) => {
  if (!pw) return "Password is required";
  if (pw.length < PW_MIN_LEN) {
    return `Password must be at least ${PW_MIN_LEN} characters`;
  }
  if (!/[a-z]/.test(pw)) return "Password must contain a lowercase letter";
  if (!/[A-Z]/.test(pw)) return "Password must contain an uppercase letter";
  if (!/[0-9]/.test(pw)) return "Password must contain a digit";
  if (!/[^A-Za-z0-9]/.test(pw)) return "Password must contain a symbol";
  return null;
};

// Very light strength score — enough to render a tiny meter without
// pulling in zxcvbn. 0..4 buckets.
const strengthScore = (pw) => {
  if (!pw) return 0;
  let s = 0;
  if (pw.length >= PW_MIN_LEN) s += 1;
  if (pw.length >= 16) s += 1;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s += 1;
  if (/[0-9]/.test(pw) && /[^A-Za-z0-9]/.test(pw)) s += 1;
  return s;
};

const STRENGTH_LABEL = ["Too weak", "Weak", "Fair", "Good", "Strong"];
const STRENGTH_COLOR = ["#ef4444", "#f97316", "#eab308", "#84cc16", "#22c55e"];

// Map coord error_code (SCK-695 taxonomy) to a user-facing message.
// Unknown codes fall back to the server-provided `message`, then to
// a generic string.
export const friendlyRedeemError = (payload, httpStatus) => {
  const code = payload?.error_code || payload?.errorCode || "";
  const raw = payload?.message || "";
  const generic = "Something went wrong. Please try again.";

  switch (String(code).toUpperCase()) {
    case "PASSWORD_TOO_WEAK":
      return {
        text:
          raw ||
          `Password does not meet the strength requirements (${PW_MIN_LEN}+ characters, upper + lower + digit + symbol).`,
        showRequestNew: false,
      };
    case "INVITATION_ALREADY_REDEEMED":
      return {
        text:
          raw ||
          "This invitation has already been redeemed. Sign in with your account.",
        showRequestNew: false,
        showSignIn: true,
      };
    case "INVITATION_EXPIRED":
      return {
        text: "This invitation has expired.",
        showRequestNew: true,
      };
    case "INVITATION_REVOKED":
      return {
        text: "This invitation has been revoked. Contact your administrator.",
        showRequestNew: false,
      };
    case "NDA_VERSION_MISMATCH":
      return {
        text:
          "The NDA was updated while you were on this page. Please refresh and accept again.",
        showRequestNew: false,
      };
    default:
      // Today (before the coord taxonomy PR lands) redemption failures
      // arrive as {error_code:"VALIDATION_EXCEPTION", message:"…"} at 500.
      // Surface the server-provided message when it looks presentable;
      // fall back to a generic string otherwise. Never leak raw JSON or
      // stack traces to the visitor.
      if (raw && raw.length > 0 && raw.length < 240 && !raw.includes("{")) {
        return { text: raw, showRequestNew: httpStatus === 410 };
      }
      return { text: generic, showRequestNew: false };
  }
};

export default function RedeemInvitationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  // NDA fetch state.
  const [ndaLoading, setNdaLoading] = useState(true);
  const [nda, setNda] = useState(null); // { version, effectiveDate, textUrl }
  const [ndaError, setNdaError] = useState(null);

  // Optional invitation preview — {email}. `null` = unknown / skip.
  const [previewEmail, setPreviewEmail] = useState(null);

  // Form state.
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [ndaChecked, setNdaChecked] = useState(false);
  const [passwordErr, setPasswordErr] = useState("");
  const [confirmErr, setConfirmErr] = useState("");

  // Submit / result state.
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null); // { text, showRequestNew, showSignIn }
  const [success, setSuccess] = useState(false);

  // Google-OAuth path state (secondary).
  const [googleBusy, setGoogleBusy] = useState(false);
  const [googleError, setGoogleError] = useState(null);

  const hasToken = token.length > 0;

  // Step 1: fetch the current NDA + optional invitation preview.
  useEffect(() => {
    if (!hasToken) return;
    let cancelled = false;

    (async () => {
      setNdaLoading(true);
      setNdaError(null);
      try {
        const res = await fetch(`${API_URL}/nda/current`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        if (!res.ok) {
          throw new Error(`Failed to load NDA (HTTP ${res.status})`);
        }
        const payload = await res.json();
        if (!cancelled) setNda(payload);
      } catch (e) {
        if (!cancelled) setNdaError(e.message || "Failed to load NDA");
      } finally {
        if (!cancelled) setNdaLoading(false);
      }
    })();

    // Preview endpoint is optional (may not yet exist server-side).
    // Any failure is silently ignored — we just don't prefill the email.
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/invitations/preview?token=${encodeURIComponent(token)}`,
          { method: "GET", headers: { "Content-Type": "application/json" } }
        );
        if (!res.ok) return;
        const payload = await res.json();
        if (!cancelled && payload?.email) setPreviewEmail(payload.email);
      } catch {
        /* preview endpoint may not exist yet — non-fatal */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hasToken, token]);

  // Auto-nav to /login a short beat after success so the user reads
  // the confirmation card and doesn't feel yanked away.
  useEffect(() => {
    if (!success) return undefined;
    const id = setTimeout(() => {
      navigate("/login?redeemed=1", { replace: true });
    }, 1800);
    return () => clearTimeout(id);
  }, [success, navigate]);

  const score = useMemo(() => strengthScore(password), [password]);

  const validate = () => {
    const pErr = validatePassword(password);
    const cErr = password !== confirm ? "Passwords do not match" : null;
    setPasswordErr(pErr || "");
    setConfirmErr(cErr || "");
    return !pErr && !cErr;
  };

  const canSubmit =
    hasToken && !ndaLoading && !!nda?.version && ndaChecked &&
    password.length > 0 && confirm.length > 0 && !submitting && !success;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validate()) return;

    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch(`${API_URL}/invitations/redeem`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          ndaAccepted: true,
          ndaVersion: nda.version,
          password,
        }),
      });
      let payload = null;
      try {
        payload = await res.json();
      } catch {
        /* server may return no body on some paths */
      }
      if (res.ok) {
        setSuccess(true);
      } else {
        setSubmitError(friendlyRedeemError(payload, res.status));
      }
    } catch (err) {
      setSubmitError({
        text: err?.message || "Network error — please try again.",
        showRequestNew: false,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleSuccess = (credentialResponse) => {
    setGoogleBusy(true);
    setGoogleError(null);
    authWithGoogle(credentialResponse, (result, status, err) => {
      setGoogleBusy(false);
      if (err || status !== 200 || !result) {
        setGoogleError(
          err ||
            (status === 401
              ? "Google sign-in was rejected. If this invitation was for a different address, sign in with a password instead."
              : "Google sign-in failed. Please try again.")
        );
        return;
      }
      // Same shape as Login.onLoginResponse. The result is the JWT;
      // stash it, decode it, land on /.
      try {
        setToken(result);
        setUserData(jwtDecode(result));
      } catch {
        /* if decode fails the app-shell will send them through /login */
      }
      navigate("/", { replace: true });
    });
  };

  // ---------- render branches ----------

  if (!hasToken) {
    return (
      <Box sx={{ width: "min(560px, 92vw)", p: 1 }}>
        <Alert severity="error" data-testid="redeem-missing-token">
          <Typography variant="subtitle1" component="div" sx={{ fontWeight: 600 }}>
            Invalid invitation link
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            This link is missing its invitation token. If you copied it
            from an email, please make sure you included the whole URL.
          </Typography>
        </Alert>
        <Box sx={{ mt: 2, textAlign: "right" }}>
          <Button component={RouterLink} to="/login" variant="text">
            Go to sign in
          </Button>
        </Box>
      </Box>
    );
  }

  if (success) {
    return (
      <Box sx={{ width: "min(560px, 92vw)", p: 1 }}>
        <Alert severity="success" data-testid="redeem-success">
          <Typography variant="subtitle1" component="div" sx={{ fontWeight: 600 }}>
            Account created
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Redirecting you to sign in&hellip;
          </Typography>
        </Alert>
        <Box sx={{ mt: 2, textAlign: "right" }}>
          <Button
            component={RouterLink}
            to="/login?redeemed=1"
            variant="contained"
            sx={brandButton()}
          >
            Sign in now
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ width: "min(560px, 92vw)", p: 1 }}
      data-testid="redeem-page"
    >
      <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
        Welcome to Clue2App
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        You&rsquo;ve been invited. Set a password to activate your account.
      </Typography>

      <Stack spacing={2}>
        <TextField
          label="Email"
          value={previewEmail || ""}
          placeholder={previewEmail ? undefined : "your invitation email"}
          InputProps={{ readOnly: true }}
          size="small"
          data-testid="redeem-email"
          helperText={
            previewEmail
              ? "Prefilled from your invitation"
              : "Your invitation email will be used to sign you in"
          }
        />

        <Box>
          <TextField
            fullWidth
            label="New password"
            name="password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (passwordErr) setPasswordErr("");
            }}
            size="small"
            required
            autoComplete="new-password"
            error={!!passwordErr}
            helperText={
              passwordErr ||
              `At least ${PW_MIN_LEN} characters, mix upper + lower + digit + symbol`
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword((s) => !s)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            inputProps={{ "data-testid": "redeem-password" }}
          />
          {/* Strength meter — 4 bars, colored to the current bucket. */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 0.5,
              mt: 0.75,
            }}
            data-testid="redeem-strength"
            aria-hidden
          >
            {[0, 1, 2, 3].map((i) => (
              <Box
                key={i}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  background:
                    score > i
                      ? STRENGTH_COLOR[Math.min(score, STRENGTH_COLOR.length - 1)]
                      : isDarkMode
                        ? "rgba(255,255,255,0.10)"
                        : "rgba(15,23,42,0.10)",
                }}
              />
            ))}
          </Box>
          {password && (
            <Typography
              variant="caption"
              sx={{
                color: STRENGTH_COLOR[Math.min(score, STRENGTH_COLOR.length - 1)],
                fontWeight: 600,
              }}
              data-testid="redeem-strength-label"
            >
              {STRENGTH_LABEL[Math.min(score, STRENGTH_LABEL.length - 1)]}
            </Typography>
          )}
        </Box>

        <TextField
          fullWidth
          label="Confirm password"
          name="confirm"
          type={showPassword ? "text" : "password"}
          value={confirm}
          onChange={(e) => {
            setConfirm(e.target.value);
            if (confirmErr) setConfirmErr("");
          }}
          size="small"
          required
          autoComplete="new-password"
          error={!!confirmErr}
          helperText={confirmErr}
          inputProps={{ "data-testid": "redeem-confirm" }}
        />

        {ndaLoading ? (
          <Skeleton
            variant="rectangular"
            height={44}
            sx={{ borderRadius: 1 }}
            data-testid="redeem-nda-loading"
          />
        ) : ndaError ? (
          <Alert severity="warning" data-testid="redeem-nda-error">
            Could not load the NDA ({ndaError}). You can still try again in a
            moment.
          </Alert>
        ) : (
          <FormControlLabel
            control={
              <Checkbox
                checked={ndaChecked}
                onChange={(e) => setNdaChecked(e.target.checked)}
                inputProps={{ "data-testid": "redeem-nda-checkbox" }}
              />
            }
            label={
              <Typography variant="body2">
                I have read and accept the{" "}
                <MuiLink
                  href={nda?.textUrl || "/legal/nda"}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  NDA
                </MuiLink>
                {nda?.version ? (
                  <>
                    {" "}
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      data-testid="redeem-nda-version"
                    >
                      (v{nda.version})
                    </Typography>
                  </>
                ) : null}
              </Typography>
            }
          />
        )}

        {submitError && (
          <Alert severity="error" data-testid="redeem-submit-error">
            {submitError.text}
            {submitError.showRequestNew && (
              <>
                {" "}
                <MuiLink component={RouterLink} to="/login">
                  Request a new invitation
                </MuiLink>
                .
              </>
            )}
            {submitError.showSignIn && (
              <>
                {" "}
                <MuiLink component={RouterLink} to="/login">
                  Sign in
                </MuiLink>
                .
              </>
            )}
          </Alert>
        )}

        <Button
          type="submit"
          variant="contained"
          disabled={!canSubmit}
          onClick={handleSubmit}
          sx={brandButton()}
          data-testid="redeem-submit"
        >
          {submitting ? (
            <>
              <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
              Creating account&hellip;
            </>
          ) : (
            "Create account"
          )}
        </Button>

        <Divider>
          <Typography
            variant="caption"
            sx={{ color: isDarkMode ? darkSurface.textMuted : "text.secondary" }}
          >
            or
          </Typography>
        </Divider>

        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Invited on a Google Workspace address? You can skip the password.
          </Typography>
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() =>
              setGoogleError("Google sign-in failed. Please try again.")
            }
            text="signin_with"
          />
          {googleBusy && (
            <Typography variant="caption" color="text.secondary">
              Signing you in&hellip;
            </Typography>
          )}
          {googleError && (
            <Alert
              severity="error"
              sx={{ width: "100%" }}
              data-testid="redeem-google-error"
            >
              {googleError}
            </Alert>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
